/**
 * Discord SDK wrapper for Discord Activities
 * Handles SDK initialization and proxied fetch requests
 */

import { DiscordSDK, DiscordSDKMock } from '@discord/embedded-app-sdk';

// Discord SDK instance (initialized once)
let discordSdk: DiscordSDK | DiscordSDKMock | null = null;
let isInitialized = false;
let initPromise: Promise<DiscordUserInfo> | null = null;
let cachedUserInfo: DiscordUserInfo | null = null;

export interface DiscordUserInfo {
  instanceId: string;
  oderId: string;
  username: string;
  channelId: string | null;
  guildId: string | null;
}

/**
 * Check if running inside Discord iframe (has frame_id param)
 */
export function isInDiscord(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has('frame_id');
}

/**
 * Check if running in mock mode (local development without Discord)
 */
export function isMockMode(): boolean {
  // Mock mode if:
  // - Not in Discord iframe (no frame_id)
  // - OR explicitly set via env var
  // - OR no client ID configured
  const noClientId = !import.meta.env.VITE_DISCORD_CLIENT_ID;
  return !isInDiscord() || import.meta.env.VITE_DISCORD_MOCK === 'true' || noClientId;
}

/**
 * Get the Discord Client ID from environment
 */
function getClientId(): string {
  const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID;
  if (!clientId) {
    // Don't log warning every time - just return empty
    return '';
  }
  return clientId;
}

/**
 * Initialize Discord SDK (call once at app start)
 * Returns user info after successful authentication
 */
export async function initializeDiscordSdk(): Promise<DiscordUserInfo> {
  // Return cached promise if already initializing
  if (initPromise) {
    return initPromise;
  }

  initPromise = doInitialize();
  return initPromise;
}

async function doInitialize(): Promise<DiscordUserInfo> {
  if (isInitialized && cachedUserInfo) {
    return cachedUserInfo;
  }

  const clientId = getClientId();

  // Use mock mode if no client ID or not in Discord iframe
  if (!clientId || isMockMode()) {
    console.log('Discord SDK: Running in mock mode (no client ID or not in Discord)');

    const mockUserId = `mock-user-${Date.now()}`;
    const mockInstanceId = `mock-instance-${Date.now()}`;

    // Create mock SDK - constructor requires (clientId, guildId, channelId, locationId)
    discordSdk = new DiscordSDKMock(clientId || 'mock', null, null, null);
    isInitialized = true;

    cachedUserInfo = {
      instanceId: mockInstanceId,
      oderId: mockUserId,
      username: 'Mock User',
      channelId: null,
      guildId: null,
    };

    return cachedUserInfo;
  }

  try {
    console.log('Discord SDK: Initializing with client ID:', clientId);

    // Create real SDK instance
    discordSdk = new DiscordSDK(clientId);

    // Wait for SDK to be ready
    await discordSdk.ready();
    console.log('Discord SDK: Ready');

    // Authorize with Discord
    const { code } = await discordSdk.commands.authorize({
      client_id: clientId,
      response_type: 'code',
      state: '',
      prompt: 'none',
      scope: ['identify', 'guilds'],
    });

    // In a real app, you'd exchange the code for a token on your server
    // For Discord Activities, we'll use a simplified flow
    // The access_token would come from your backend after exchanging the code
    console.log('Discord SDK: Got authorization code', code);

    // For now, extract user info from the SDK instance directly
    // In production, you'd call your backend to exchange the code
    isInitialized = true;

    cachedUserInfo = {
      instanceId: discordSdk.instanceId,
      oderId: `discord-user-${Date.now()}`, // Would come from token exchange
      username: 'Discord User',
      channelId: discordSdk.channelId || null,
      guildId: discordSdk.guildId || null,
    };

    return cachedUserInfo;
  } catch (error) {
    console.error('Discord SDK: Initialization failed', error);

    // Fallback to mock mode on error
    console.log('Discord SDK: Falling back to mock mode');
    const mockUserId = `fallback-user-${Date.now()}`;

    cachedUserInfo = {
      instanceId: `fallback-instance-${Date.now()}`,
      oderId: mockUserId,
      username: 'Fallback User',
      channelId: null,
      guildId: null,
    };

    isInitialized = true;
    return cachedUserInfo;
  }
}

/**
 * Get the current Discord SDK instance
 */
export function getDiscordSdk(): DiscordSDK | DiscordSDKMock | null {
  return discordSdk;
}

/**
 * Perform a fetch request through Discord's proxy (for Activities)
 * Falls back to regular fetch in mock mode
 */
export async function discordFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  // In mock mode or if SDK not initialized, use regular fetch
  if (isMockMode() || !discordSdk || !isInitialized) {
    return fetch(url, options);
  }

  try {
    // Use Discord SDK's proxied fetch
    // The URL should be relative to the mapped prefix (e.g., /.proxy/api/...)
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (error) {
    console.error('Discord fetch error:', error);
    throw error;
  }
}

/**
 * Get the API base URL (uses Discord proxy in production)
 */
export function getApiBaseUrl(): string {
  if (isMockMode()) {
    // Local development - connect directly to worker
    return import.meta.env.VITE_API_URL || 'https://alchemy-duel-worker.laim0999716349.workers.dev';
  }

  // In Discord - use the proxy URL
  return import.meta.env.VITE_API_URL || '/.proxy/api';
}
